import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  brokersBySectorQueryOptions,
  sectorBrokersQueryOptions,
  sectorFlowQueryOptions,
} from '@/features/brokers/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { sectorFlowSearchSchema } from '@/screens/brokers/search'

/**
 * The `/brokers/sector-flow` loader.
 *
 * MAIN: whichever of the two branches the entry URL's `?sector=` picks — the
 * whole-market overview (`sectorFlowQueryOptions`) when it is absent, or one
 * sector's ranked brokers (`sectorBrokersQueryOptions`) when it is present.
 * `SectorFlowScreen` renders one of two DIFFERENT child components for these
 * (see its own doc comment on why a conditional hook call cannot do this),
 * so the loader has to pick the same branch the URL will mount.
 *
 * `NO_FLOORSHEET_DATA` is a LEGITIMATE, routine wait here — sector flow reads
 * only the nightly precompute (`SectorFlowEmpty`'s own doc comment) — so
 * `ensureRouteData` never rejects on it: `sectorFlowQueryOptions` /
 * `sectorBrokersQueryOptions` (queries.ts) catch that one code and resolve
 * `null`, per docs/route-loading.md, "Not-found answers". This loader settles
 * normally either way, and `SectorFlowScreen.tsx`'s own components draw the
 * "isn't ready yet" copy off `data === null`, the same shape Stock draws "not
 * found" off `company === null`. The route's `errorComponent` only ever sees a
 * GENUINE failure now.
 *
 * SECONDARY, overview branch only, started before the `await` and only on a
 * real navigation (not a hover preload — docs/route-loading.md, "Secondary
 * reads"): the "Broker by sector" grid (`brokersBySectorQueryOptions`). Its
 * card draws its own skeleton, failure and not-summarised state, so it cannot
 * blank the page. And, once the overview's own read is in, the per-sector read
 * for its most-money-in sector — the summary strip's "Brokers buying" cell,
 * which holds a bar of its own until it lands. The drilldown has no secondary
 * read: its one query IS the page.
 *
 * `period` is pinned to the literal `'today'` by the route's own schema (see
 * `sectorFlowSearchSchema`), and `sector` is read from `location.search`
 * through that same schema, not `loaderDeps` — see docs/route-loading.md,
 * "Search params". A member picking a different sector from the overview
 * navigates with a search-only change, which does not re-run this loader; the
 * drilldown component reads its own query from there on.
 */
export async function loadSectorFlow({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const { period, sector } = sectorFlowSearchSchema.parse(location.search)
  if (sector === undefined) {
    if (!preload) prefetchRouteData(queryClient, brokersBySectorQueryOptions(period))
    const flow = await ensureRouteData(queryClient, sectorFlowQueryOptions(period))
    const topSector = flow?.mostIn?.sector
    if (!preload && topSector !== undefined) {
      prefetchRouteData(queryClient, sectorBrokersQueryOptions(topSector, period))
    }
  } else {
    await ensureRouteData(queryClient, sectorBrokersQueryOptions(sector, period))
  }
}
